'use client';

import type { ILocationResult } from '@components/searchCity';
import type { LeagueEvent } from '@interfaces/APITravel';
import { getLatLng, searchLocation } from '@utils/location';
import { useDebounce } from '@uidotdev/usehooks';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
  CommandLoading,
} from '@v2/ui/Command/Command';
import { Arena, Building05, SearchMD } from '@v2/ui/Icon/General';
import { MarkerPin01 } from '@v2/ui/Icon/MapsAndTravel';
import { Input } from '@v2/ui/Input/Input';
import { Popover, PopoverAnchor, PopoverContent } from '@v2/ui/Popover';
import { RadioGroup, RadioGroupItem } from '@v2/ui/RadioGroup/RadioGroup';
import { typographyVariants } from '@v2/ui/Typography';
import { cn } from '@v2/utils';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import useSWR from 'swr';
import {
  coerceHotelSearchArea,
  venueCityName,
  venueDisplayCity,
  type HotelSearchArea,
} from '../hotel-search-area';
import { isMapCoordinate } from '../hotel-presentation';

type PlaceSuggestion = {
  PlaceId: string;
  Text: string;
};

type Props = {
  event?: LeagueEvent;
  downtown: ILocationResult | null;
  value: HotelSearchArea;
  customQuery?: string;
  showLandmarkSearch?: boolean;
  onSelectArea: (area: 'stadium' | 'downtown') => void;
  onSelectCustom?: (location: ILocationResult) => void;
  onCustomQueryChange?: (query: string) => void;
};

function AreaCard({
  id,
  value,
  selected,
  icon,
  title,
  primary,
  secondary,
}: {
  id: string;
  value: 'stadium' | 'downtown';
  selected: boolean;
  icon: ReactNode;
  title: string;
  primary: string;
  secondary?: string;
}) {
  return (
    <label
      htmlFor={id}
      className={cn(
        'flex min-h-[7.5rem] cursor-pointer flex-col gap-0.5 rounded-xl border p-3 text-left transition-colors focus-within:ring-2 focus-within:ring-primary-200',
        selected
          ? 'border-primary-500 bg-primary-50/50 ring-1 ring-primary-500'
          : 'border-gray-200 bg-white hover:border-gray-300'
      )}
    >
      <RadioGroupItem value={value} id={id} className="sr-only" />
      <span className="flex size-10 items-center justify-center rounded-lg border border-gray-200 text-gray-800">
        {icon}
      </span>
      <span className="mt-1 block text-sm font-semibold text-gray-900">
        {title}
      </span>
      <span className="block truncate text-sm text-gray-800">{primary}</span>
      {secondary ? (
        <span className="block truncate text-xs text-gray-500">
          {secondary}
        </span>
      ) : null}
    </label>
  );
}

export function HotelAreaPicker({
  event,
  downtown,
  value,
  customQuery = '',
  showLandmarkSearch = false,
  onSelectArea,
  onSelectCustom,
  onCustomQueryChange,
}: Props) {
  const area = coerceHotelSearchArea(value);
  const venueName = event?.venue?.name ?? 'the stadium';
  const city = venueDisplayCity(event) || venueCityName(event);
  const locality = event?.venue?.address?.locality?.trim();
  const region = event?.venue?.address?.region?.trim();
  const stadiumSub =
    [locality, region].filter(Boolean).join(', ') ||
    event?.venue?.location ||
    '';
  const downtownName = downtown?.city_name || city || 'the city';
  const radioValue = area === 'stadium' || area === 'downtown' ? area : '';
  const showDowntown = Boolean(downtown);
  const helper =
    area === 'downtown' && downtown
      ? `Hotels around downtown ${downtownName}.`
      : area === 'custom' && customQuery
        ? `Hotels around ${customQuery}.`
        : `Hotels around ${venueName}${stadiumSub ? ` in ${stadiumSub}` : ''}.`;

  return (
    <div className="flex flex-col gap-4">
      <RadioGroup
        value={radioValue}
        onValueChange={(next) => {
          if (next === 'stadium' || next === 'downtown') onSelectArea(next);
        }}
        className={cn(
          'grid gap-3',
          showDowntown ? 'grid-cols-2' : 'grid-cols-1'
        )}
        aria-label="Hotel search area"
      >
        <AreaCard
          id="hotel-area-stadium"
          value="stadium"
          selected={radioValue === 'stadium'}
          icon={<Arena size="22" stroke="black" />}
          title="Near stadium"
          primary={venueName}
          secondary={stadiumSub || undefined}
        />
        {showDowntown ? (
          <AreaCard
            id="hotel-area-downtown"
            value="downtown"
            selected={radioValue === 'downtown'}
            icon={
              <Building05 size="22" pathProps={{ stroke: 'currentColor' }} />
            }
            title="City center"
            primary={downtown?.name ?? `Downtown ${downtownName}`}
            secondary={downtownName}
          />
        ) : null}
      </RadioGroup>
      <p className="flex items-start gap-1.5 text-sm text-gray-600">
        <MarkerPin01
          size="18"
          className="mt-0.5 shrink-0 text-gray-500"
          pathProps={{ stroke: 'currentColor' }}
        />
        <span>{helper}</span>
      </p>
      {showLandmarkSearch ? (
        <LandmarkSearch
          query={customQuery}
          area={area}
          onQueryChange={onCustomQueryChange}
          onSelect={onSelectCustom}
        />
      ) : null}
    </div>
  );
}

function LandmarkSearch({
  query,
  area,
  onQueryChange,
  onSelect,
}: {
  query: string;
  area: HotelSearchArea;
  onQueryChange?: (query: string) => void;
  onSelect?: (location: ILocationResult) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const suppressFocusOpenRef = useRef(false);
  const selectionToken = useRef(0);
  const lastEmittedText = useRef(query);
  const [searchValue, setSearchValue] = useState(query);
  const [isOpen, setIsOpen] = useState(false);
  const debounced = useDebounce(searchValue, 400);
  const canSearch = debounced.trim().length >= 2;
  const { data, isValidating } = useSWR<PlaceSuggestion[]>(
    canSearch ? ['hotel-area-geocode', debounced] : null,
    () => searchLocation(debounced),
    {
      revalidateOnFocus: false,
      dedupingInterval: 30_000,
    }
  );

  useEffect(() => {
    if (query !== lastEmittedText.current) {
      selectionToken.current += 1;
      lastEmittedText.current = query;
    }
    setSearchValue(query);
    if (!query.trim()) setIsOpen(false);
  }, [query]);

  useEffect(() => {
    if (area !== 'custom') selectionToken.current += 1;
  }, [area]);

  useEffect(
    () => () => {
      selectionToken.current += 1;
    },
    []
  );

  const suggestions = Array.isArray(data)
    ? data.filter(
        (item): item is PlaceSuggestion =>
          Boolean(item?.PlaceId) && Boolean(item?.Text)
      )
    : [];
  const showPopover = isOpen && canSearch;

  const handleQuery = (next: string) => {
    selectionToken.current += 1;
    lastEmittedText.current = next;
    setSearchValue(next);
    onQueryChange?.(next);
    setIsOpen(next.trim().length >= 2);
  };

  const handleSelect = async (place: PlaceSuggestion) => {
    const token = ++selectionToken.current;
    lastEmittedText.current = place.Text;
    setSearchValue(place.Text);
    onQueryChange?.(place.Text);
    setIsOpen(false);
    suppressFocusOpenRef.current = true;
    requestAnimationFrame(() => inputRef.current?.focus());
    const parts = place.Text.split(', ');
    try {
      const result = await getLatLng(place.PlaceId);
      if (token !== selectionToken.current) return;
      const point = result?.Geometry?.Point;
      const longitude = point?.[0];
      const latitude = point?.[1];
      if (longitude == null || latitude == null) return;
      const lng = Number(longitude);
      const lat = Number(latitude);
      if (!isMapCoordinate(lat, lng)) return;
      if (inputRef.current && inputRef.current.value !== place.Text) return;
      onSelect?.({
        id: place.PlaceId,
        name: place.Text,
        city_name: parts[0] ?? '',
        country_name: parts[parts.length - 1] ?? '',
        latitude: lat,
        longitude: lng,
      });
    } catch {
      // Keep the typed query; Apply stays disabled until coords resolve.
    }
  };

  return (
    <div>
      <p className="mb-2 text-sm font-semibold text-gray-800">
        Or search by address or landmark
      </p>
      <Popover
        open={showPopover}
        onOpenChange={(open) => {
          setIsOpen(open);
          if (open) {
            requestAnimationFrame(() => inputRef.current?.focus());
          }
        }}
      >
        <PopoverAnchor asChild>
          <div className="w-full">
            <Input
              ref={inputRef}
              value={searchValue}
              onChange={(event) => handleQuery(event.target.value)}
              onFocus={() => {
                if (suppressFocusOpenRef.current) {
                  suppressFocusOpenRef.current = false;
                  return;
                }
                if (canSearch) setIsOpen(true);
              }}
              onKeyDown={(event) => {
                if (event.key === 'Escape') setIsOpen(false);
              }}
              placeholder="Enter address, neighborhood, or landmark"
              aria-label="Search by address, neighborhood, or landmark"
              aria-expanded={showPopover}
              aria-controls="hotel-area-landmark-results"
              autoComplete="off"
              startIcon={<SearchMD size="20" className="text-gray-400" />}
              className="h-12 rounded-xl bg-white"
            />
          </div>
        </PopoverAnchor>
        <PopoverContent
          className="popover-content-width-same-as-its-trigger min-w-full rounded-lg border border-gray-200 bg-white p-0 shadow-xl"
          style={{ zIndex: 70 }}
          sideOffset={8}
          align="start"
          onOpenAutoFocus={(event) => {
            event.preventDefault();
            requestAnimationFrame(() => inputRef.current?.focus());
          }}
        >
          <Command shouldFilter={false}>
            <CommandList
              id="hotel-area-landmark-results"
              data-vaul-no-drag
              className="max-h-[min(280px,var(--radix-popover-content-available-height))] overflow-y-auto"
            >
              {isValidating ? (
                <CommandLoading>Searching locations…</CommandLoading>
              ) : null}
              {!isValidating && suggestions.length === 0 ? (
                <CommandEmpty>No location found.</CommandEmpty>
              ) : null}
              <CommandGroup>
                {suggestions.map((option) => (
                  <CommandItem
                    key={option.PlaceId}
                    value={option.Text}
                    className="cursor-pointer items-start whitespace-normal px-4 py-3"
                    onSelect={() => {
                      void handleSelect(option);
                    }}
                  >
                    <span
                      className={cn(
                        typographyVariants({ size: 'tmd' }),
                        'font-semibold text-gray-800'
                      )}
                    >
                      {option.Text}
                    </span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
